import { CodeHighlightTabs } from '@mantine/code-highlight';
import { Tooltip } from '@mantine/core';
import { type Options } from 'prettier';
import babelPlugin from 'prettier/plugins/babel';
import esTreePlugin from 'prettier/plugins/estree';
import prettier from 'prettier/standalone';
import { useEffect, useState } from 'react';
import { api } from '~/trpc-extra/api';
import { PlaywrightLogo } from '../icons/Playwright';

import { STABLY_SDK_VERSION } from '@stablyai/codegen-agent-constants';

export const prettierFormat: Options = {
  parser: 'babel',
  plugins: [babelPlugin, esTreePlugin],
  useTabs: false,
  semi: true,
  singleQuote: true,
};

export default function CodeViewPanel({ testContentId }: { testContentId: string }) {
  const { data: testContent } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContentId },
  );
  const { data: code, isLoading } = api.testContent.getCode.useQuery(
    { testContentId, testName: testContent?.test.name, wrapCode: true },
    { enabled: !!testContent },
  );
  const [formattedCode, setFormattedCode] = useState('');

  const [, setActiveTab] = useState(0);

  useEffect(() => {
    if (!code) {
      return;
    }
    // Add custom drag or fill functions if they're used in the code. A new line is added to separate each function from the rest of the code.
    prettier
      .format(code, prettierFormat)
      .then(setFormattedCode)
      .catch((e) => {
        console.warn('prettier format error', e);
      });
  }, [code, isLoading]);

  return (
    <div className="relative h-full w-full overflow-x-auto">
      <Tooltip label="Please ensure you are using the latest version">
        <a
          href="https://www.npmjs.com/package/@stablyai/playwright-test"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-[11px] right-12 z-10 rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 hover:text-gray-800 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
        >
          stably-playwright-sdk {STABLY_SDK_VERSION}
        </a>
      </Tooltip>
      <CodeHighlightTabs
        // NOTE: This pb-10 is a hack to ensure that we don't extend outside the window
        // This entier components horizontal scroll is weird, can't find a good fix (something to do with it having a
        // ScrollArea inside of it)
        className="h-full w-full pb-10"
        // oxlint-disable-next-line react/no-unstable-nested-components
        getFileIcon={testContent?.test.testType === 'MOBILE_APP' ? undefined : () => <PlaywrightLogo />}
        onTabChange={setActiveTab}
        withCopyButton
        code={[{ fileName: 'test.ts', code: formattedCode || code || '', language: 'tsx' }].filter(
          (x) => x !== undefined,
        )}
      />
    </div>
  );
}
